import React, { useEffect, useRef, useState } from 'react';
import { IconButton, Tooltip } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

export const COPY_FAILED_MESSAGE = "Couldn't copy. Select the text and copy it manually.";
const COPIED_SHOWN_MS = 1500;
const FAILED_SHOWN_MS = 4000;

// Copies `text` to the clipboard and says what happened: a tick with "Copied"
// for a moment on success; a red mark with the failure message when the
// clipboard is unavailable or refuses the write. The tooltip is held open
// while either is showing and the button's accessible name follows it, so a
// click is never silent (L73).
function CopyButton({ text, title = 'Copy', size = 'small', sx }) {
  const [outcome, setOutcome] = useState(null);
  const [hovered, setHovered] = useState(false);
  const timerRef = useRef(null);
  const mountedRef = useRef(true);
  useEffect(() => () => {
    mountedRef.current = false;
    clearTimeout(timerRef.current);
  }, []);

  const handleClick = async () => {
    clearTimeout(timerRef.current);
    let next = 'copied';
    try {
      if (typeof navigator.clipboard?.writeText !== 'function') {
        throw new Error('The clipboard is not available here.');
      }
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.error('Copy to clipboard failed:', err);
      next = 'failed';
    }
    if (!mountedRef.current) return;
    setOutcome(next);
    timerRef.current = setTimeout(
      () => setOutcome(null),
      next === 'copied' ? COPIED_SHOWN_MS : FAILED_SHOWN_MS,
    );
  };

  const label = outcome === 'copied' ? 'Copied' : outcome === 'failed' ? COPY_FAILED_MESSAGE : title;

  return (
    <Tooltip
      title={label}
      open={hovered || outcome !== null}
      onOpen={() => setHovered(true)}
      onClose={() => setHovered(false)}
    >
      <IconButton size={size} onClick={handleClick} aria-label={label} sx={sx}>
        {outcome === 'copied' && <CheckIcon fontSize="small" color="success" />}
        {outcome === 'failed' && <ErrorOutlineIcon fontSize="small" color="error" />}
        {outcome === null && <ContentCopyIcon fontSize="small" />}
      </IconButton>
    </Tooltip>
  );
}

export default CopyButton;
